import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, json } from '../api/client'
import type { TextArtifact, TextKind, TextOverview } from '../api/types'
import { episodeKey, refreshEpisode } from './useShows'

const textKey = (ws: string, id: string) => [...episodeKey(ws, id), 'text'] as const

export function useText(ws: string, id: string, enabled: boolean) {
  return useQuery({
    queryKey: textKey(ws, id),
    queryFn: () => api<TextOverview>(`/workspaces/${ws}/episodes/${id}/text`),
    enabled,
  })
}

/** Queue a text job. `reviseVersion` rewrites that version following `instruction`. */
export function useGenerateText(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { kind: TextKind; instruction?: string; reviseVersion?: number }) =>
      api<{ job_id: string }>(`/workspaces/${ws}/episodes/${id}/text/${input.kind}`, {
        method: 'POST',
        body: json({ instruction: input.instruction || null, revise_version: input.reviseVersion ?? null }),
      }),
    onSuccess: () => refreshEpisode(queryClient, ws, id),
  })
}

/** Save an edit as the next version. */
export function useSaveText(ws: string, id: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { kind: TextKind; content: Record<string, unknown>; baseVersion: number }) =>
      api<TextArtifact>(`/workspaces/${ws}/episodes/${id}/text/${input.kind}`, {
        method: 'PUT',
        body: json({ content: input.content, base_version: input.baseVersion }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: textKey(ws, id) }),
  })
}

/** One earlier version; versions never change, so it is cached for good. */
export function useTextVersion(ws: string, id: string, kind: TextKind, version: number | null) {
  return useQuery({
    queryKey: [...textKey(ws, id), kind, version],
    queryFn: () => api<TextArtifact>(`/workspaces/${ws}/episodes/${id}/text/${kind}/versions/${version}`),
    enabled: version != null,
    staleTime: Infinity,
  })
}
